import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, DoorOpen, CalendarDays, Users, DollarSign, Copy, Check,
  RefreshCw, ChevronLeft, ChevronRight, Building2, Home, Link2
} from 'lucide-react';
import { format, addDays, subDays, differenceInDays, isToday, isWeekend, startOfDay } from 'date-fns';

const DAY_W = 42;

const SOURCE_COLORS = {
  manual: '#2563EB',
  ical_airbnb: '#F472B6',
  ical_booking: '#1E3A5F',
  ical_vrbo: '#8B5CF6',
  block: '#FB7185',
};

function getBarColor(source) {
  if (source?.includes('airbnb')) return SOURCE_COLORS.ical_airbnb;
  if (source?.includes('booking')) return SOURCE_COLORS.ical_booking;
  if (source?.includes('vrbo')) return SOURCE_COLORS.ical_vrbo;
  if (source?.includes('ical')) return '#8B5CF6';
  if (source === 'block') return SOURCE_COLORS.block;
  return SOURCE_COLORS.manual;
}

export default function UnitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedExport, setCopiedExport] = useState(false);

  useEffect(() => {
    api.get(`/units/${id}`).then(r => setUnit(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const rangeStart = useMemo(() => subDays(today, 3), [today]);
  const rangeEnd = useMemo(() => addDays(today, 40), [today]);
  const days = useMemo(() => {
    const d = [];
    let c = rangeStart;
    while (c <= rangeEnd) { d.push(c); c = addDays(c, 1); }
    return d;
  }, [rangeStart, rangeEnd]);

  const icalExportUrl = unit?.ical_export_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/units/${id}/ical-export?token=${unit.ical_export_token}`
    : null;

  const copyExportUrl = () => {
    if (icalExportUrl) {
      navigator.clipboard.writeText(icalExportUrl);
      setCopiedExport(true);
      setTimeout(() => setCopiedExport(false), 2000);
    }
  };

  const getReservationBars = () => {
    if (!unit) return [];
    const bars = [];
    (unit.reservations || []).forEach(r => {
      if (r.status === 'cancelled') return;
      const checkIn = startOfDay(new Date(r.check_in));
      const checkOut = startOfDay(new Date(r.check_out));
      const startOff = differenceInDays(checkIn, rangeStart);
      const dur = differenceInDays(checkOut, checkIn);
      if (startOff + dur < 0 || startOff > days.length) return;
      const cs = Math.max(0, startOff);
      const ce = Math.min(days.length, startOff + dur);
      bars.push({
        id: r.id, left: cs * DAY_W, width: (ce - cs) * DAY_W,
        color: getBarColor(r.source),
        label: r.guest_name || r.notes || r.source?.replace('ical_', '') || 'Guest',
        status: r.status,
      });
    });
    (unit.calendar_blocks || []).forEach(b => {
      const s = startOfDay(new Date(b.start_date));
      const e = startOfDay(new Date(b.end_date));
      const startOff = differenceInDays(s, rangeStart);
      const dur = differenceInDays(e, s);
      if (startOff + dur < 0 || startOff > days.length) return;
      const cs = Math.max(0, startOff);
      const ce = Math.min(days.length, startOff + dur);
      bars.push({
        id: `block-${b.id}`, left: cs * DAY_W, width: (ce - cs) * DAY_W,
        color: SOURCE_COLORS.block, label: b.notes || 'Blocked', status: 'block',
      });
    });
    return bars;
  };

  const statusColors = {
    available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    unavailable: 'bg-slate-100 text-slate-600',
    maintenance: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;
  if (!unit) return <div className="text-center py-12 text-slate-400">Unit not found</div>;

  const bars = getReservationBars();
  const activeRes = (unit.reservations || []).filter(r => ['confirmed', 'checked_in'].includes(r.status)).length;
  const totalRes = (unit.reservations || []).length;
  const icalCount = (unit.ical_sources || []).length;

  return (
    <div data-testid="unit-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/units')} className="mb-4 text-slate-600" data-testid="back-to-units-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />Back to Units
      </Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold font-heading text-slate-900 tracking-tight" data-testid="unit-detail-name">{unit.name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge className={statusColors[unit.status] || ''} data-testid="unit-detail-status">{unit.status}</Badge>
            {unit.property_name && <span className="flex items-center gap-1 text-sm text-slate-500"><Building2 className="w-3.5 h-3.5" />{unit.property_name}</span>}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="unit-stat-bedrooms">
          <div className="flex items-center gap-2 text-blue-600 mb-1"><DoorOpen className="w-4 h-4" /><span className="text-xs font-medium">Bedrooms</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">{unit.bedrooms}</p>
          <p className="text-xs text-slate-500 mt-0.5">{unit.bathrooms} bath</p>
        </div>
        <div className="stat-card" data-testid="unit-stat-guests">
          <div className="flex items-center gap-2 text-purple-600 mb-1"><Users className="w-4 h-4" /><span className="text-xs font-medium">Max Guests</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">{unit.max_guests}</p>
        </div>
        <div className="stat-card" data-testid="unit-stat-price">
          <div className="flex items-center gap-2 text-emerald-600 mb-1"><DollarSign className="w-4 h-4" /><span className="text-xs font-medium">Nightly Rate</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">R{(unit.base_price || 0).toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-0.5">Clean: R{(unit.cleaning_fee || 0).toLocaleString()}</p>
        </div>
        <div className="stat-card" data-testid="unit-stat-reservations">
          <div className="flex items-center gap-2 text-amber-600 mb-1"><CalendarDays className="w-4 h-4" /><span className="text-xs font-medium">Reservations</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">{activeRes}<span className="text-sm text-slate-500 font-normal"> / {totalRes}</span></p>
          <p className="text-xs text-slate-500 mt-0.5">active / total</p>
        </div>
      </div>

      {/* Amenities */}
      {unit.amenities?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-2">Amenities</h3>
          <div className="flex flex-wrap gap-2">
            {unit.amenities.map(a => <span key={a} className="text-xs px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full">{a}</span>)}
          </div>
        </div>
      )}

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="calendar" data-testid="unit-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="ical" data-testid="unit-tab-ical">iCal ({icalCount})</TabsTrigger>
          <TabsTrigger value="reservations" data-testid="unit-tab-reservations">Reservations ({totalRes})</TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="unit-calendar">
            {/* Date header */}
            <div className="flex border-b border-slate-200">
              {days.map((day, i) => (
                <div
                  key={i}
                  className={`flex flex-col items-center justify-center py-2 border-r border-slate-100 last:border-r-0 ${isWeekend(day) ? 'bg-slate-50' : 'bg-white'}`}
                  style={{ width: DAY_W, minWidth: DAY_W }}
                >
                  {day.getDate() === 1 && <span className="text-[9px] text-blue-600 font-bold leading-none">{format(day, 'MMM')}</span>}
                  <span className={`text-xs font-semibold leading-tight ${isToday(day) ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-slate-800'}`}>
                    {format(day, 'd')}
                  </span>
                  <span className={`text-[9px] leading-none mt-0.5 ${isToday(day) ? 'text-blue-600 font-bold' : 'text-slate-400'}`}>{format(day, 'EEEEE')}</span>
                </div>
              ))}
            </div>
            {/* Timeline bar area */}
            <div className="relative" style={{ height: 56, minWidth: days.length * DAY_W }}>
              <div className="flex absolute inset-0">
                {days.map((day, i) => (
                  <div key={i} className={`border-r border-slate-100 ${isWeekend(day) ? 'bg-slate-50/50' : ''}`} style={{ width: DAY_W, minWidth: DAY_W, height: '100%' }} />
                ))}
              </div>
              {bars.map(bar => (
                <div
                  key={bar.id}
                  className="absolute flex items-center overflow-hidden text-[10px] font-medium text-white whitespace-nowrap"
                  style={{ left: bar.left, width: Math.max(bar.width, 8), top: 10, height: 28, backgroundColor: bar.color, borderRadius: 4, paddingLeft: 8, paddingRight: 8, zIndex: 5 }}
                  title={bar.label}
                  data-testid={`unit-cal-bar-${bar.id}`}
                >
                  <span className="truncate">{bar.label}</span>
                </div>
              ))}
              {bars.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">No bookings in this period</div>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-3 text-xs">
            {[{ c: '#2563EB', l: 'Manual' }, { c: '#F472B6', l: 'Airbnb' }, { c: '#1E3A5F', l: 'Booking' }, { c: '#8B5CF6', l: 'iCal' }, { c: '#FB7185', l: 'Block' }].map(x => (
              <div key={x.l} className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: x.c }} /><span className="text-slate-500">{x.l}</span></div>
            ))}
          </div>
        </TabsContent>

        {/* iCal Tab */}
        <TabsContent value="ical">
          {/* Export URL */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="ical-export-section">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-slate-900">iCal Export URL</h3>
            </div>
            <p className="text-xs text-slate-500 mb-3">Share this URL with Airbnb, Booking.com, or VRBO to export this unit's calendar.</p>
            {icalExportUrl ? (
              <div className="flex gap-2">
                <Input value={icalExportUrl} readOnly className="text-xs font-mono bg-slate-50 flex-1" data-testid="ical-export-url-input" />
                <Button variant="outline" size="sm" onClick={copyExportUrl} data-testid="copy-export-url-btn">
                  {copiedExport ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-600" />Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-slate-400">No export token available for this unit.</p>
            )}
          </div>

          {/* iCal Sources */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">Import Sources</h3>
            </div>
            {(unit.ical_sources || []).length > 0 ? (
              <div className="divide-y divide-slate-100">
                {unit.ical_sources.map(src => (
                  <div key={src.id} className="px-5 py-3 flex items-center justify-between" data-testid={`unit-ical-src-${src.id}`}>
                    <div>
                      <Badge variant="outline" className="capitalize text-xs">{src.platform}</Badge>
                      <p className="text-xs text-slate-500 mt-1 truncate max-w-sm">{src.url}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={src.sync_status === 'success' ? 'bg-emerald-50 text-emerald-700' : src.sync_status === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}>
                        {src.sync_status}
                      </Badge>
                      <p className="text-[10px] text-slate-400 mt-1">{src.events_count || 0} events</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-5 py-8 text-center text-xs text-slate-400">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 opacity-30" />
                No iCal import sources configured.<br />
                <Button variant="link" size="sm" className="mt-1 text-blue-600" onClick={() => navigate('/ical')}>Manage iCal Sources</Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Reservations Tab */}
        <TabsContent value="reservations">
          <div className="table-wrap">
            <table className="w-full data-table min-w-[600px]">
              <thead><tr><th>Guest</th><th>Check-in</th><th>Check-out</th><th>Source</th><th>Status</th></tr></thead>
              <tbody>
                {(unit.reservations || []).map(r => (
                  <tr key={r.id} data-testid={`unit-res-row-${r.id}`}>
                    <td className="font-medium text-slate-900">{r.guest_name || r.notes || 'Guest'}</td>
                    <td>{r.check_in ? format(new Date(r.check_in), 'MMM dd, yyyy') : 'N/A'}</td>
                    <td>{r.check_out ? format(new Date(r.check_out), 'MMM dd, yyyy') : 'N/A'}</td>
                    <td><Badge variant="outline" className="text-xs capitalize">{r.source?.replace('ical_', '')}</Badge></td>
                    <td><Badge className={
                      r.status === 'confirmed' ? 'bg-blue-50 text-blue-700' :
                      r.status === 'checked_in' ? 'bg-emerald-50 text-emerald-700' :
                      r.status === 'checked_out' ? 'bg-slate-100 text-slate-600' :
                      r.status === 'cancelled' ? 'bg-red-50 text-red-700' :
                      'bg-amber-50 text-amber-700'
                    }>{r.status}</Badge></td>
                  </tr>
                ))}
                {(unit.reservations || []).length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">No reservations</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
